/**
 * Copyright 2026 Salesforce, Inc.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
import { useEffect } from 'react';

import type { DatadogRumConfig } from '@/types/config';

import { initDatadogRum } from './datadog-rum.utils';

interface DatadogRumProps {
    /** `app.datadogRum` from the root loader's config. */
    config: DatadogRumConfig;
}

/**
 * Datadog Real User Monitoring (UCR-3022). Renders nothing.
 *
 * The agent itself is fetched by the `<head>` stub in `root.tsx`, which is what lets it start
 * capturing the page load before React hydrates. This component only supplies the configuration —
 * `DD_RUM.onReady()` bridges the two, so their order doesn't matter.
 *
 * Mounted once, gated on config, so RUM is present on every page. Not deferred to an idle frame
 * like `<Attentive>`: the point of RUM is to measure the page load, and deferring initialisation
 * would skew the very numbers it exists to report. The network cost is already off the critical
 * path because the agent script is `async`.
 *
 * Client navigations are handled by `<DatadogViewTracker>`, not here.
 */
export default function DatadogRum({ config }: DatadogRumProps) {
    useEffect(() => {
        initDatadogRum(config);
    }, [config]);

    return null;
}
